/**
 * Notes list — spec §5-F6, §8.1 `/notes`.
 *
 * Personal notes and the daily note. Project docs use `ProjectDocsPage`, which
 * shares the row component but adds the one-level tree F6 calls for.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { CalendarDays, FileText, NotebookPen, Paperclip, Pin, Plus, Search } from 'lucide-react';
import type { NoteScope } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { useCreateNote, useDailyNote, useNotes, type NoteView } from './api';

const SCOPES: { value: NoteScope | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'personal', label: 'Personal' },
  { value: 'meeting', label: 'Meetings' },
  { value: 'project', label: 'Project' },
  { value: 'task', label: 'Task' },
];

export function NoteRow({ note, to }: { note: NoteView; to: string }) {
  return (
    <Link
      data-testid="note-row"
      to={to}
      className="flex items-start gap-2.5 border-b border-[var(--border)] px-3 py-2.5 transition-colors duration-[120ms] hover:bg-[var(--surface-2)]"
    >
      <FileText className="mt-0.5 size-4 shrink-0 text-[var(--text-muted)]" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-medium">{note.title}</span>
          {note.isPinned && <Pin className="size-3 text-[var(--accent)]" aria-label="Pinned" />}
          {note.isDailyNote && <Badge tone="neutral">Daily</Badge>}
          {/* V4 F-2: worth knowing a note carries files before you open it. */}
          {(note.attachmentCount ?? 0) > 0 && (
            <span
              className="flex items-center gap-0.5 text-[11px] vt-muted"
              data-testid="note-attachment-count"
              title={`${note.attachmentCount} attachment${note.attachmentCount === 1 ? '' : 's'}`}
            >
              <Paperclip className="size-3" />
              {note.attachmentCount}
            </span>
          )}
        </div>
        {note.bodyText ? (
          <p className="line-clamp-1 text-[12px] vt-muted">{note.bodyText.slice(0, 160)}</p>
        ) : (
          <p className="text-[12px] italic vt-muted">Empty</p>
        )}
      </div>
      <div className="shrink-0 text-right text-[11px] vt-muted">
        <Badge tone="neutral">{note.scope}</Badge>
        <div className="mt-1">{new Date(note.updatedAt).toLocaleDateString()}</div>
      </div>
    </Link>
  );
}

export function NotesPage() {
  const [scope, setScope] = useState<NoteScope | 'all'>('all');
  const [q, setQ] = useState('');
  const { data: notes, isLoading } = useNotes({
    ...(scope === 'all' ? {} : { scope }),
    ...(q.trim() ? { q: q.trim() } : {}),
  });
  const create = useCreateNote();
  const daily = useDailyNote();
  const navigate = useNavigate();

  const pinned = (notes ?? []).filter((n) => n.isPinned);
  const rest = (notes ?? []).filter((n) => !n.isPinned);

  return (
    <>
      <PageHeader
        title="Notes"
        subtitle="Durable knowledge — the things that stay useful after the conversation ends."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                const note = await daily.mutateAsync();
                navigate(`/notes/${note._id}`);
              }}
            >
              <CalendarDays />
              Today’s note
            </Button>
            <Button
              variant="primary"
              size="sm"
              data-testid="new-note"
              onClick={async () => {
                const note = await create.mutateAsync({ scope: 'personal', title: 'Untitled note' });
                navigate(`/notes/${note._id}`);
              }}
            >
              <Plus />
              New note
            </Button>
          </>
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--text-muted)]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search notes…"
                aria-label="Search notes"
                className="h-7 w-56 pl-7"
              />
            </div>
            <div className="flex gap-1">
              {SCOPES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setScope(s.value)}
                  aria-pressed={scope === s.value}
                  className={`rounded-[6px] px-2 py-1 text-[12px] transition-colors duration-[120ms] ${
                    scope === s.value
                      ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent)]'
                      : 'vt-muted hover:bg-[var(--surface-2)]'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        }
      />

      <div className="p-4">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : (notes ?? []).length === 0 ? (
          <EmptyState
            icon={<NotebookPen className="size-6" />}
            title={q ? 'No notes match that' : 'No notes yet'}
            description={
              q
                ? 'Try a shorter search, or a different scope.'
                : 'Meeting minutes, research, decisions — anything worth keeping after the chat scrolls away.'
            }
            action={
              <Button
                variant="primary"
                onClick={async () => {
                  const note = await create.mutateAsync({ scope: 'personal', title: 'Untitled note' });
                  navigate(`/notes/${note._id}`);
                }}
              >
                <Plus />
                Write the first note
              </Button>
            }
          />
        ) : (
          <div className="vt-surface overflow-hidden" data-testid="notes-list">
            {pinned.length > 0 && (
              <>
                <div className="bg-[var(--surface-2)] px-3 py-1 text-[11px] font-medium uppercase tracking-wide vt-muted">
                  Pinned
                </div>
                {pinned.map((note) => (
                  <NoteRow key={note._id} note={note} to={`/notes/${note._id}`} />
                ))}
              </>
            )}
            {rest.map((note) => (
              <NoteRow key={note._id} note={note} to={`/notes/${note._id}`} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
